// RigSentinel — Drilling Fluid Management (Phase RCE-3)
//
// MUD PROPERTY TRI-STATE SCHEMA
// -----------------------------
// Every mud property carries three engineering states:
//
//   * target   — the engineered ideal (from the Mud Program)
//   * current  — the most recent live reading (typically from the DMR)
//   * planned  — the value the engineer plans to migrate toward for the
//                upcoming interval
//
// This tri-state design supports future trend analysis and AI-assisted
// recommendations without requiring schema migrations. Phase RCE-3
// resolves `current` against the latest DMR (if available) and falls
// back to `target` when nothing has been measured yet.

export const MUD_PROPERTY_KEYS = Object.freeze([
  "weightInPpg",
  "plasticViscosityCp",
  "yieldPointLbf100ft2",
  "gelStrength10sLbf100ft2",
  "gelStrength10minLbf100ft2",
  "fluidLossMl30min",
  "pH",
  "sandContentPct",
  "oilWaterRatio",
  "electricalStabilityV",
  "htHpFluidLossMl",
]);

export const MUD_PROPERTY_LABELS = Object.freeze({
  weightInPpg:               "Mud Weight (ppg)",
  plasticViscosityCp:        "Plastic Viscosity (cP)",
  yieldPointLbf100ft2:       "Yield Point (lbf/100 ft²)",
  gelStrength10sLbf100ft2:   "10-s Gel Strength (lbf/100 ft²)",
  gelStrength10minLbf100ft2: "10-min Gel Strength (lbf/100 ft²)",
  fluidLossMl30min:          "Fluid Loss (mL / 30 min)",
  pH:                        "pH",
  sandContentPct:            "Sand Content (%)",
  oilWaterRatio:             "Oil / Water Ratio",
  electricalStabilityV:      "Electrical Stability (V)",
  htHpFluidLossMl:           "HTHP Fluid Loss (mL)",
});

export const MUD_PROPERTY_RCE_PATHS = Object.freeze(
  MUD_PROPERTY_KEYS.reduce((acc, k) => {
    acc[k] = `mud.${k}`;
    return acc;
  }, {}),
);

/** Empty tri-state value. Every field defaults to "" so the UI can
 *  render "—" when nothing has been recorded yet. */
export function emptyTriState() {
  return { target: "", current: "", planned: "" };
}

/** Empty property table — keyed by MUD_PROPERTY_KEYS. */
export function emptyPropertyTable() {
  const out = {};
  for (const k of MUD_PROPERTY_KEYS) out[k] = emptyTriState();
  return out;
}

/**
 * Pick the "live" value for a property given its tri-state. Resolution
 * order: current → target → planned → "". The resolver / derivers use
 * this to publish the single most credible value.
 */
export function pickLiveValue(triState) {
  if (!triState || typeof triState !== "object") return { value: "", source: "unknown" };
  if (triState.current !== "" && triState.current != null) return { value: triState.current, source: "current" };
  if (triState.target  !== "" && triState.target  != null) return { value: triState.target,  source: "target"  };
  if (triState.planned !== "" && triState.planned != null) return { value: triState.planned, source: "planned" };
  return { value: "", source: "unknown" };
}

/** Validate a tri-state. Every leg must be either blank or a finite
 *  number for the numeric properties; pH / O-W ratio also accept simple
 *  numeric values. RCE-3 keeps the validation deliberately gentle —
 *  field engineers enter rounded numbers and trailing decimals routinely. */
export function validateTriState(triState, allowText = false) {
  if (!triState || typeof triState !== "object") return { ok: false, errors: ["Tri-state is missing."] };
  const errs = [];
  for (const leg of ["target", "current", "planned"]) {
    const v = triState[leg];
    if (v === "" || v == null) continue;
    if (allowText) continue;
    if (!Number.isFinite(Number(v))) errs.push(`${leg} must be a number if entered.`);
  }
  return { ok: errs.length === 0, errors: errs };
}
